import React, { useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';

import {
	Main,
	SideNavigation,
	SideNavigationMenuItems,
	FullLayout,
	getTranslatedString,
	useTranslations
} from '@bosch/bt-ui';

import './DefaultLayout.css';
import MainAppBar from '../components/MainAppBar';
import MainFooter from '../components/MainFooter';
import withErrorHandler from '../components/ErrorHandler';
import HomeIcon from '../../icons/home.svg';
import SimIcon from '../../icons/simcard.svg';
import CompanyIcon from '../../icons/company.svg';
import CloudShareIcon from '../../icons/cloud-share.svg';
import ControlPanelIcon from '../../icons/control-panel.svg';
import PromptUnloading from '../components/PromptUnloading';
import UserServiceIcon from '../../icons/user-service.svg';


const DefaultLayoutContext = React.createContext();

export const DefaultLayoutContextProvider = DefaultLayoutContext.Provider;

export function useDefaultLayout() {
	return useContext(DefaultLayoutContext);
}

const DefaultLayout = (props) => {
	const {
		token,
		activeMenuId,
		pageTitle,
		isDirty,
		setIsDirty,
		children
	} = props;
	
	const location = useLocation();

	const translations = useTranslations();
	const {
		isSideNavigationOpen,
		setIsSideNavigationOpen
	} = useDefaultLayout();

	const url = SUPPORTCONSOLE_API_URL;
	const isDev = url.includes('dev');
    const isTest = url.includes('test');
	const hasEngAdminRole = token?.roles?.includes("eng-admin");
	const canAccessPanelEnvironmentPage = (
		(hasEngAdminRole && !isDev && !isTest)
		|| isDev
		|| isTest
	);

	const menus = [
		{
			key: 'home',
			text: getTranslatedString('Home', translations),
			url: '/',
			icon: HomeIcon
		},
		{
			key: 'sim-detail-page',
			text: getTranslatedString('SIM Information', translations),
			url: '/sim-detail',
			icon: SimIcon
		},
		{
			key: 'sim-management',
			text: getTranslatedString('SIM Management', translations),
			url: '/simmanagement',
			icon: SimIcon
		},
		{
			key: 'company-page',
			text: getTranslatedString('Company Management', translations),
			url: '/company',
			icon: CompanyIcon
		},
		{
			key: 'sim-activation-detail-page',
			text: getTranslatedString('Sim Activity Logs', translations),
			url: '/simactivationdetail',
			icon: SimIcon
		},
		{
			key: 'panel-management-page',
			text: getTranslatedString('title-panel-management-page', translations),
			url: '/panel-management',
			icon: ControlPanelIcon
		},
		// Access to the panel environment page is restricted to users with the "eng-admin" role in the production environment.
		// However, it is accessible to all users in the development and testing environments.
		...canAccessPanelEnvironmentPage ? [
			{
			key: 'panel-environment-page',
			text: getTranslatedString('title-panel-environment-page', translations),
			url: '/panel-environment',
			icon: ControlPanelIcon
			}] : [],
		{
			key: 'csr-management-page',
			text: getTranslatedString('title-csr-management', translations),
			url: '/csr-management',
			icon: CloudShareIcon
		},
		{
			key: 'user-management-page',
			text: getTranslatedString('title-user-management', translations),
			url: '/user-management',
			icon: UserServiceIcon
		},
		{
			key: 'user-action-detail-page',
			text: getTranslatedString('title-user-action-detail', translations),
			url: '/user-action-detail',
			icon: UserServiceIcon
		},
		{
			key: 'wc-metrics-page',
			text: getTranslatedString('WebConnect Metrics', translations),
			url: '/wc-metrics-redirect',
			icon: ControlPanelIcon
		},
	];

	useEffect(() => {
		// Everytime refresh DefaultLayout then reset dirty page
        setIsDirty(false);
    }, [setIsDirty, location.pathname]);

	return (
		<FullLayout
			sideNavigation={
				<SideNavigation
					isOpen={isSideNavigationOpen}
					setIsOpen={setIsSideNavigationOpen}
					appName='Menu'
				>
					<SideNavigationMenuItems
						menus={menus}
						activeMenuId={activeMenuId}
					/>
				</SideNavigation>
			}
		>
			<PromptUnloading isDirty={isDirty} />
			<MainAppBar token={token} isDirty={isDirty} pageTitle={pageTitle} />

			<Main>
				{children}
			</Main>

			<MainFooter />
		</FullLayout>
	);
}

export default withErrorHandler(DefaultLayout);